import { applyVat } from "./vat";
import { freshDraft, newId } from "./freshDraft";
import { resolveDocumentDefaults } from "./documentDefaults";
import type { Customer, LineItem, Receivable, ReceivableDraft, UploadedInvoice } from "./types";

/**
 * Builds a new draft "based on" a previously issued/drafted invoice — the shared
 * carry-over rule both apps must agree on (US1.AC4, issue #816 follow-up).
 *
 * Payment terms and both references carry over: they're relationship-level facts
 * about this customer, not this specific invoice. `invoiceDate` deliberately resets to
 * `freshDraft()`'s own value (today's date) rather than the source's. The presentation
 * choices (interest rate, free text, payment method) carry over too — dropping them
 * would send a differently-worded invoice than the one copied.
 *
 * FIN-1580 (invoice-document-i18n.AC7/EC5): `documentLanguage`/`currency` carry over the
 * same way — a relationship-level choice about how this customer is billed, not
 * recomputed from the customer's current `country_code` each time.
 */
export function baseOnExistingInvoice(source: Receivable, customer: Customer | null): ReceivableDraft {
  return {
    ...freshDraft(),
    customerId: source.customerId,
    sourceInvoiceId: source.id,
    recurrence: source.recurrence,
    paymentTermsDays: source.paymentTermsDays,
    ourReference: source.ourReference,
    theirReference: source.theirReference,
    interestRatePercent: source.interestRatePercent,
    freeText: source.freeText,
    paymentMethod: source.paymentMethod,
    // Carried as-is; the form drops it if the account is no longer connected (carryOverReceivingAccount).
    receivingBankAccountId: source.receivingBankAccountId,
    documentLanguage: source.documentLanguage,
    currency: source.currency,
    lines: applyVat(customer, source.lines.map((l) => ({ ...l, id: newId("l") })), source.documentLanguage),
  };
}

/**
 * The editor's "already classified" seed for a copied draft: line id → trimmed description,
 * for each line that already carries a VAT category (FIN-890). Without it the editor
 * re-asks the classifier for a category the source was issued with. A line with no
 * category, or an inconclusive (null) one, is left out so it is still classified.
 */
export function classifiedLineDescriptions(lines: LineItem[]): Record<string, string> {
  return Object.fromEntries(
    lines.filter((l) => l.vatCategory != null).map((l) => [l.id, l.description.trim()]),
  );
}

/**
 * Builds a new draft from an "uploaded from another system" invoice. Deliberately leaves
 * `sourceInvoiceId` unset: `sourceInvoiceId` is threaded through to the backend as
 * `sourceReceivableId`, whose real FK (`receivables_source_receivable_id_fkey`) requires a
 * `receivables.id` — `source.id` here is an uploaded document's `financial_documents.id`, a
 * different id space entirely. An invoice started from an uploaded document has no source
 * receivable at all, not an invalid one.
 *
 * FIN-1580 (invoice-document-i18n.EC6): an uploaded/extracted source carries no structured
 * language/currency of its own — this defaults exactly like a brand-new invoice would
 * (`resolveDocumentDefaults`), never assumes SEK or the source document's own presentation.
 */
export function baseOnUploadedInvoice(source: UploadedInvoice, customer: Customer | null): ReceivableDraft {
  const defaults = customer ? resolveDocumentDefaults(customer) : null;
  return {
    ...freshDraft(),
    customerId: source.customerId,
    ...(defaults ? { documentLanguage: defaults.documentLanguage, currency: defaults.currency } : {}),
    lines: applyVat(customer, source.lines.map((l) => ({ ...l, id: newId("l") })), defaults?.documentLanguage ?? "sv"),
  };
}
